/** TanStack Query hooks for revision: revisable content and paginated revision history. */
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { ReviseHistoryItem, PaginatedResponse } from '@formency/shared';
import type { RevisableContent } from '@/shared/types';

// Re-export types so existing consumers keep working
export type { RevisableContent } from '@/shared/types';

// ─── Query key factory ────────────────────────────────────────────────────────

export const reviseKeys = {
  all: ['f', 'revise'] as const,
  content: () => [...reviseKeys.all, 'content'] as const,
  history: (pageSize: number) => [...reviseKeys.all, 'history', pageSize] as const,
};

// ─── Queries ──────────────────────────────────────────────────────────────────

/** Fetch courses and personal lessons available for revision. */
export function useRevisableContent() {
  return useQuery<RevisableContent>({
    queryKey: reviseKeys.content(),
    queryFn: () => api.get<RevisableContent>('/me/study-sessions/revisable-content'),
    staleTime: 60_000,
  });
}

/** Fetch paginated revision session history (infinite scroll). */
export function useReviseHistory(pageSize = 10) {
  return useInfiniteQuery<PaginatedResponse<ReviseHistoryItem>>({
    queryKey: reviseKeys.history(pageSize),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<ReviseHistoryItem>>('/me/study-sessions/revise/history', {
        page: pageParam,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
  });
}
